import { DragEvent, FC, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { CheckBox, ChevronLeft, ChevronRight, Close, Add, ExpandMore } from '@mui/icons-material';
import { SCUIButton } from '../../../atomic/atoms';
import JiraTrackerQuery, {
    PersonalWorkLogEntry,
    WorkLogPriorityColor,
} from '../../../services/query/jiraTracker/jiraTrackerQuery.services';
import JiraTrackerCommand from '../../../services/command/jiraTracker/jiraTrackerCommand.services';

const DAILY_TARGET_MINUTES = 495; // 8h 15m
const WEEK_TARGET_MINUTES = DAILY_TARGET_MINUTES * 5;

const PRIORITY_COLORS: WorkLogPriorityColor[] = ['green', 'yellow', 'orange', 'red'];
const MAX_PERIOD_DAYS = 31;

type PeriodMode = 'single' | 'range';

const parseDateKey = (dateKey: string): Date | null => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return null;
    const d = new Date(`${dateKey}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
};

const enumerateDateKeys = (fromKey: string, toKey: string): string[] => {
    const from = parseDateKey(fromKey);
    const to = parseDateKey(toKey);
    if (!from || !to || from > to) return [fromKey];

    const keys: string[] = [];
    const cursor = new Date(from);
    while (cursor <= to && keys.length < MAX_PERIOD_DAYS) {
        keys.push(toDateKey(cursor));
        cursor.setDate(cursor.getDate() + 1);
    }
    return keys;
};

const startOfWeekMonday = (date: Date): Date => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay(); // 0 Sun .. 6 Sat
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return d;
};

const addDays = (date: Date, days: number): Date => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

const toDateKey = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const formatMinutes = (minutes: number): string => {
    const safe = Math.max(0, minutes || 0);
    const h = Math.floor(safe / 60);
    const m = safe % 60;
    return `${h}h ${m}m`;
};

const formatShortDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}`;
};

const formatRangeLabel = (from: Date, to: Date): string => {
    const fmt = (d: Date) => {
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = String(d.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
    };
    return `${fmt(from)} - ${fmt(to)}`;
};

const normalizePriorityColor = (value?: string | null): WorkLogPriorityColor => {
    const normalized = (value || '').trim().toLowerCase();
    if (PRIORITY_COLORS.includes(normalized as WorkLogPriorityColor)) {
        return normalized as WorkLogPriorityColor;
    }
    return 'green';
};

type DayColumn = {
    date: Date;
    dateKey: string;
    dayLabelKey: string;
    entries: PersonalWorkLogEntry[];
    totalMinutes: number;
    isToday: boolean;
};

type FormState = {
    personalWorkLogEntryId?: number | null;
    workDate: string;
    periodMode: PeriodMode;
    periodEndDate: string;
    title: string;
    ticketId: string;
    durationMinutes: number;
    technicalNotes: string;
    notes: string;
    isCompleted: boolean;
    sortOrder: number;
    priorityColor: WorkLogPriorityColor;
};

const emptyForm = (workDate: string): FormState => ({
    personalWorkLogEntryId: null,
    workDate,
    periodMode: 'single',
    periodEndDate: workDate,
    title: '',
    ticketId: '',
    durationMinutes: DAILY_TARGET_MINUTES,
    technicalNotes: '',
    notes: '',
    isCompleted: false,
    sortOrder: 0,
    priorityColor: 'green',
});

const normalizeEntry = (raw: any): PersonalWorkLogEntry | null => {
    if (!raw) return null;
    const id = raw.personalWorkLogEntryId ?? raw.PersonalWorkLogEntryId;
    if (id == null) return null;
    const workDate = String(raw.workDate ?? raw.WorkDate ?? '').substring(0, 10);
    return {
        personalWorkLogEntryId: Number(id),
        workDate,
        title: raw.title ?? raw.Title ?? '',
        ticketId: raw.ticketId ?? raw.TicketId ?? null,
        durationMinutes: Number(raw.durationMinutes ?? raw.DurationMinutes ?? 0),
        technicalNotes: raw.technicalNotes ?? raw.TechnicalNotes ?? null,
        notes: raw.notes ?? raw.Notes ?? null,
        isCompleted: Boolean(raw.isCompleted ?? raw.IsCompleted),
        sortOrder: Number(raw.sortOrder ?? raw.SortOrder ?? 0),
        priorityColor: normalizePriorityColor(raw.priorityColor ?? raw.PriorityColor),
        createdDate: raw.createdDate ?? raw.CreatedDate,
        updatedDate: raw.updatedDate ?? raw.UpdatedDate,
    };
};

const normalizeEntries = (response: any): PersonalWorkLogEntry[] => {
    const list = response?.entries ?? response?.Entries ?? response ?? [];
    if (!Array.isArray(list)) return [];
    return list.map(normalizeEntry).filter(Boolean) as PersonalWorkLogEntry[];
};

export const WeeklyWorkLogPanel: FC = () => {
    const { t } = useTranslation();
    const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(new Date()));
    const [entries, setEntries] = useState<PersonalWorkLogEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [technicalNotesExpanded, setTechnicalNotesExpanded] = useState(false);
    const [form, setForm] = useState<FormState>(() => emptyForm(toDateKey(new Date())));
    const [dragOverDateKey, setDragOverDateKey] = useState<string | null>(null);
    const [draggingEntryId, setDraggingEntryId] = useState<number | null>(null);
    const technicalNotesRef = useRef<HTMLTextAreaElement | null>(null);
    const skipCardClickRef = useRef(false);

    const syncTechnicalNotesHeight = useCallback(() => {
        const el = technicalNotesRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.max(el.scrollHeight, 160)}px`;
    }, []);

    useEffect(() => {
        if (!modalOpen || !technicalNotesExpanded) return;
        // Accordion açıldıktan / içerik değiştikten sonra tam yüksekliği hesapla
        const id = window.requestAnimationFrame(() => syncTechnicalNotesHeight());
        return () => window.cancelAnimationFrame(id);
    }, [modalOpen, technicalNotesExpanded, form.technicalNotes, syncTechnicalNotesHeight]);

    const weekEnd = useMemo(() => addDays(weekStart, 4), [weekStart]);
    const todayKey = toDateKey(new Date());

    const loadWeek = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await JiraTrackerQuery.GetPersonalWorkLogWeek({
                FromDate: toDateKey(weekStart),
                ToDate: toDateKey(weekEnd),
            });
            setEntries(normalizeEntries(response));
        } catch (err: any) {
            setError(err?.message || t('jiraTracker.workLog.error.loadFailed'));
            setEntries([]);
        } finally {
            setLoading(false);
        }
    }, [weekStart, weekEnd, t]);

    useEffect(() => {
        loadWeek();
    }, [loadWeek]);

    const upsertLocalEntry = (entry: PersonalWorkLogEntry) => {
        setEntries((prev) => {
            const index = prev.findIndex((e) => e.personalWorkLogEntryId === entry.personalWorkLogEntryId);
            if (index >= 0) {
                const next = [...prev];
                next[index] = entry;
                return next;
            }
            // Yeni kayitta Id henuz gelmemisse (0) yine de goster
            if (!entry.personalWorkLogEntryId) {
                return [
                    ...prev,
                    {
                        ...entry,
                        personalWorkLogEntryId: -Date.now(),
                    },
                ];
            }
            return [...prev, entry];
        });
    };

    const dayColumns: DayColumn[] = useMemo(() => {
        const dayKeys = [
            'jiraTracker.workLog.days.mon',
            'jiraTracker.workLog.days.tue',
            'jiraTracker.workLog.days.wed',
            'jiraTracker.workLog.days.thu',
            'jiraTracker.workLog.days.fri',
        ];

        return Array.from({ length: 5 }, (_, index) => {
            const date = addDays(weekStart, index);
            const dateKey = toDateKey(date);
            const dayEntries = entries
                .filter((e) => (e.workDate || '').substring(0, 10) === dateKey)
                .sort((a, b) => {
                    const colorOrder = PRIORITY_COLORS.indexOf(
                        normalizePriorityColor(a.priorityColor)
                    );
                    const colorOrderB = PRIORITY_COLORS.indexOf(
                        normalizePriorityColor(b.priorityColor)
                    );
                    // Kritik (red) ustte
                    if (colorOrder !== colorOrderB) {
                        return colorOrderB - colorOrder;
                    }
                    return (a.sortOrder || 0) - (b.sortOrder || 0);
                });
            const totalMinutes = dayEntries.reduce((sum, e) => sum + (e.durationMinutes || 0), 0);
            return {
                date,
                dateKey,
                dayLabelKey: dayKeys[index],
                entries: dayEntries,
                totalMinutes,
                isToday: dateKey === todayKey,
            };
        });
    }, [weekStart, entries, todayKey]);

    const weekTotalMinutes = useMemo(
        () => dayColumns.reduce((sum, d) => sum + d.totalMinutes, 0),
        [dayColumns]
    );

    const todayColumn = dayColumns.find((d) => d.isToday);
    const todayIncomplete = todayColumn?.entries.filter((e) => !e.isCompleted) || [];
    const showTodayReminder = Boolean(todayColumn);

    const openCreate = (dateKey: string) => {
        setForm(emptyForm(dateKey));
        setTechnicalNotesExpanded(false);
        setModalOpen(true);
    };

    const openEdit = (entry: PersonalWorkLogEntry) => {
        const workDate = (entry.workDate || '').substring(0, 10);
        setForm({
            personalWorkLogEntryId: entry.personalWorkLogEntryId,
            workDate,
            periodMode: 'single',
            periodEndDate: workDate,
            title: entry.title || '',
            ticketId: entry.ticketId || '',
            durationMinutes: entry.durationMinutes ?? 0,
            technicalNotes: entry.technicalNotes || '',
            notes: entry.notes || '',
            isCompleted: !!entry.isCompleted,
            sortOrder: entry.sortOrder || 0,
            priorityColor: normalizePriorityColor(entry.priorityColor),
        });
        setTechnicalNotesExpanded(Boolean(entry.technicalNotes?.trim()));
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setTechnicalNotesExpanded(false);
    };

    const handleSave = async () => {
        if (!form.title.trim()) {
            setError(t('jiraTracker.workLog.error.titleRequired'));
            return;
        }

        const isNewEntry = !form.personalWorkLogEntryId || form.personalWorkLogEntryId <= 0;
        const usePeriod = isNewEntry && form.periodMode === 'range';
        const endDate = usePeriod ? form.periodEndDate || form.workDate : form.workDate;

        if (usePeriod) {
            if (!parseDateKey(form.workDate) || !parseDateKey(endDate)) {
                setError(t('jiraTracker.workLog.error.invalidPeriod'));
                return;
            }
            if (endDate < form.workDate) {
                setError(t('jiraTracker.workLog.error.periodEndBeforeStart'));
                return;
            }
            const dayCount =
                Math.floor(
                    (parseDateKey(endDate)!.getTime() - parseDateKey(form.workDate)!.getTime()) /
                        (24 * 60 * 60 * 1000)
                ) + 1;
            if (dayCount > MAX_PERIOD_DAYS) {
                setError(t('jiraTracker.workLog.error.periodTooLong', { max: MAX_PERIOD_DAYS }));
                return;
            }
        }

        const dates = usePeriod ? enumerateDateKeys(form.workDate, endDate) : [form.workDate];

        setSaving(true);
        setError(null);
        try {
            for (const workDate of dates) {
                const result = await JiraTrackerCommand.UpsertPersonalWorkLogEntry({
                    PersonalWorkLogEntryId: usePeriod ? null : form.personalWorkLogEntryId || null,
                    WorkDate: workDate,
                    Title: form.title.trim(),
                    TicketId: form.ticketId.trim() || null,
                    DurationMinutes: Number(form.durationMinutes) || 0,
                    TechnicalNotes: form.technicalNotes || null,
                    Notes: form.notes || null,
                    IsCompleted: form.isCompleted,
                    SortOrder: form.sortOrder || 0,
                    PriorityColor: form.priorityColor,
                });

                const saved =
                    normalizeEntry(result?.entry ?? result?.Entry ?? result) ||
                    normalizeEntry({
                        personalWorkLogEntryId: form.personalWorkLogEntryId || -Date.now(),
                        workDate,
                        title: form.title.trim(),
                        ticketId: form.ticketId.trim() || null,
                        durationMinutes: Number(form.durationMinutes) || 0,
                        technicalNotes: form.technicalNotes || null,
                        notes: form.notes || null,
                        isCompleted: form.isCompleted,
                        sortOrder: form.sortOrder || 0,
                        priorityColor: form.priorityColor,
                    });

                if (saved) {
                    upsertLocalEntry(saved);
                }
            }

            setModalOpen(false);
            await loadWeek();
        } catch (err: any) {
            setError(err?.message || t('jiraTracker.workLog.error.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!form.personalWorkLogEntryId) return;
        setSaving(true);
        setError(null);
        try {
            await JiraTrackerCommand.DeletePersonalWorkLogEntry({
                PersonalWorkLogEntryId: form.personalWorkLogEntryId,
            });
            setEntries((prev) => prev.filter((e) => e.personalWorkLogEntryId !== form.personalWorkLogEntryId));
            setModalOpen(false);
            await loadWeek();
        } catch (err: any) {
            setError(err?.message || t('jiraTracker.workLog.error.deleteFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleFormKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Enter' || saving) return;

        const target = event.target as HTMLElement | null;
        const tagName = target?.tagName?.toLowerCase();
        const isTextarea = tagName === 'textarea';

        // Tek satirli alanlarda Enter = Kaydet; cok satirli alanlarda Ctrl/Cmd+Enter = Kaydet
        if (isTextarea && !(event.ctrlKey || event.metaKey)) {
            return;
        }

        event.preventDefault();
        void handleSave();
    };

    const handleCardDragStart = (event: DragEvent, entry: PersonalWorkLogEntry) => {
        if (!entry.personalWorkLogEntryId || entry.personalWorkLogEntryId <= 0 || saving) {
            event.preventDefault();
            return;
        }

        setDraggingEntryId(entry.personalWorkLogEntryId);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(entry.personalWorkLogEntryId));
        event.dataTransfer.setData('application/x-work-log-entry-id', String(entry.personalWorkLogEntryId));
    };

    const handleCardDragEnd = () => {
        setDraggingEntryId(null);
        setDragOverDateKey(null);
        // Drag sonrası click ile modal açılmasın
        skipCardClickRef.current = true;
        window.setTimeout(() => {
            skipCardClickRef.current = false;
        }, 0);
    };

    const handleColumnDragOver = (event: DragEvent, dateKey: string) => {
        const types = new Set(Array.from(event.dataTransfer.types as unknown as string[]));
        const isWorkLogDrag =
            draggingEntryId != null ||
            types.has('application/x-work-log-entry-id') ||
            types.has('text/plain');
        if (!isWorkLogDrag) return;

        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        if (dragOverDateKey !== dateKey) {
            setDragOverDateKey(dateKey);
        }
    };

    const handleColumnDragLeave = (event: DragEvent, dateKey: string) => {
        const related = event.relatedTarget as Node | null;
        if (related && (event.currentTarget as HTMLElement).contains(related)) {
            return;
        }
        setDragOverDateKey((prev) => (prev === dateKey ? null : prev));
    };

    const handleMoveEntryToDate = async (entryId: number, targetDateKey: string) => {
        const entry = entries.find((e) => e.personalWorkLogEntryId === entryId);
        if (!entry || entryId <= 0) return;

        const currentDateKey = (entry.workDate || '').substring(0, 10);
        if (currentDateKey === targetDateKey) return;

        const previousEntries = entries;
        const optimistic: PersonalWorkLogEntry = { ...entry, workDate: targetDateKey };
        upsertLocalEntry(optimistic);
        setDragOverDateKey(null);
        setDraggingEntryId(null);

        setSaving(true);
        setError(null);
        try {
            const result = await JiraTrackerCommand.UpsertPersonalWorkLogEntry({
                PersonalWorkLogEntryId: entry.personalWorkLogEntryId,
                WorkDate: targetDateKey,
                Title: entry.title || '',
                TicketId: entry.ticketId || null,
                DurationMinutes: entry.durationMinutes ?? 0,
                TechnicalNotes: entry.technicalNotes || null,
                Notes: entry.notes || null,
                IsCompleted: !!entry.isCompleted,
                SortOrder: entry.sortOrder || 0,
                PriorityColor: normalizePriorityColor(entry.priorityColor),
            });

            const saved =
                normalizeEntry(result?.entry ?? result?.Entry ?? result) ||
                normalizeEntry({ ...entry, workDate: targetDateKey });

            if (saved) {
                upsertLocalEntry(saved);
            }
        } catch (err: any) {
            setEntries(previousEntries);
            setError(err?.message || t('jiraTracker.workLog.error.moveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleColumnDrop = (event: DragEvent, targetDateKey: string) => {
        event.preventDefault();
        setDragOverDateKey(null);

        const rawId =
            event.dataTransfer.getData('application/x-work-log-entry-id') ||
            event.dataTransfer.getData('text/plain');
        const entryId = Number(rawId);
        if (!entryId || Number.isNaN(entryId)) return;

        void handleMoveEntryToDate(entryId, targetDateKey);
    };

    const handleCardClick = (entry: PersonalWorkLogEntry) => {
        if (skipCardClickRef.current) return;
        openEdit(entry);
    };

    return (
        <Box className="weekly-work-log">
            <Box className="weekly-work-log__header">
                <SCUIButton
                    type="secondary"
                    sizeType="default"
                    size="s"
                    text={t('jiraTracker.workLog.today')}
                    onClick={() => setWeekStart(startOfWeekMonday(new Date()))}
                />
                <Box className="weekly-work-log__nav">
                    <IconButton
                        size="small"
                        onClick={() => setWeekStart((prev) => addDays(prev, -7))}
                        aria-label={t('jiraTracker.workLog.prevWeek')}
                    >
                        <ChevronLeft />
                    </IconButton>
                    <Box className="weekly-work-log__range">
                        <Typography variant="body2" color="text.secondary">
                            {formatRangeLabel(weekStart, weekEnd)}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                            {formatMinutes(weekTotalMinutes)} / {formatMinutes(WEEK_TARGET_MINUTES)}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={() => setWeekStart((prev) => addDays(prev, 7))}
                        aria-label={t('jiraTracker.workLog.nextWeek')}
                    >
                        <ChevronRight />
                    </IconButton>
                </Box>
            </Box>

            {showTodayReminder && (
                <Alert
                    severity={todayIncomplete.length > 0 || (todayColumn?.entries.length || 0) === 0 ? 'info' : 'success'}
                    sx={{ mb: 2 }}
                >
                    {(todayColumn?.entries.length || 0) === 0
                        ? t('jiraTracker.workLog.reminder.emptyToday')
                        : todayIncomplete.length > 0
                          ? t('jiraTracker.workLog.reminder.incompleteToday', {
                                count: todayIncomplete.length,
                                titles: todayIncomplete.map((e) => e.title).join(', '),
                            })
                          : t('jiraTracker.workLog.reminder.allDoneToday')}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Box className={`weekly-work-log__grid${loading ? ' is-loading' : ''}`}>
                {dayColumns.map((col) => (
                    <Box
                        key={col.dateKey}
                        className={`weekly-work-log__column${col.isToday ? ' is-today' : ''}${
                            col.totalMinutes > 0 ? ' has-entries' : ''
                        }${dragOverDateKey === col.dateKey ? ' is-drop-target' : ''}`}
                        onDragOver={(e) => handleColumnDragOver(e, col.dateKey)}
                        onDragLeave={(e) => handleColumnDragLeave(e, col.dateKey)}
                        onDrop={(e) => handleColumnDrop(e, col.dateKey)}
                    >
                        <Box className="weekly-work-log__column-header">
                            <Box>
                                <Typography component="span" className="weekly-work-log__day-name">
                                    {t(col.dayLabelKey)}
                                </Typography>
                                <Typography component="span" className="weekly-work-log__day-date">
                                    {formatShortDate(col.date)}
                                </Typography>
                            </Box>
                            <Typography className="weekly-work-log__day-total">
                                {formatMinutes(col.totalMinutes)} {t('jiraTracker.workLog.of')}{' '}
                                {formatMinutes(DAILY_TARGET_MINUTES)}
                            </Typography>
                        </Box>

                        <Box className="weekly-work-log__column-body">
                            <Typography className="weekly-work-log__section-label">
                                {t('jiraTracker.workLog.workRecords')}
                            </Typography>

                            {col.entries.map((entry) => {
                                const color = normalizePriorityColor(entry.priorityColor);
                                const completedClass = entry.isCompleted ? ' is-completed' : '';
                                const isDragging = draggingEntryId === entry.personalWorkLogEntryId;
                                const canDrag =
                                    Boolean(entry.personalWorkLogEntryId) &&
                                    entry.personalWorkLogEntryId > 0 &&
                                    !saving;
                                return (
                                    <Box
                                        key={entry.personalWorkLogEntryId}
                                        className={`weekly-work-log__card weekly-work-log__card--${color}${completedClass}${
                                            isDragging ? ' is-dragging' : ''
                                        }`}
                                        draggable={canDrag}
                                        onDragStart={(e) => handleCardDragStart(e, entry)}
                                        onDragEnd={handleCardDragEnd}
                                        onClick={() => handleCardClick(entry)}
                                        role="button"
                                        tabIndex={0}
                                        title={t('jiraTracker.workLog.dragHint')}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') openEdit(entry);
                                        }}
                                    >
                                        <Typography
                                            className={`weekly-work-log__card-priority weekly-work-log__card-priority--${color}`}
                                        >
                                            {t(`jiraTracker.workLog.priority.${color}`)}
                                        </Typography>
                                        <Typography className="weekly-work-log__card-title">{entry.title}</Typography>
                                        <Box className="weekly-work-log__card-footer">
                                            <Box className="weekly-work-log__card-ticket">
                                                {entry.isCompleted ? (
                                                    <CheckBox fontSize="inherit" color="error" />
                                                ) : (
                                                    <CheckBox fontSize="inherit" color="disabled" />
                                                )}
                                                <span>{entry.ticketId || '—'}</span>
                                            </Box>
                                            <Typography className="weekly-work-log__card-duration">
                                                {formatMinutes(entry.durationMinutes)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}

                            <button
                                type="button"
                                className="weekly-work-log__add"
                                onClick={() => openCreate(col.dateKey)}
                                aria-label={t('jiraTracker.workLog.addEntry')}
                            >
                                <Add />
                            </button>
                        </Box>
                    </Box>
                ))}
            </Box>

            <Dialog
                open={modalOpen}
                onClose={closeModal}
                fullWidth
                maxWidth="md"
                className="weekly-work-log__dialog"
            >
                <DialogTitle className="weekly-work-log__modal-title">
                    {t('jiraTracker.workLog.modalTitle')}
                    <IconButton onClick={closeModal} size="small" aria-label={t('jiraTracker.workLog.cancel')}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers onKeyDown={handleFormKeyDown}>
                    <Box className="weekly-work-log__form">
                        <TextField
                            fullWidth
                            size="small"
                            label={t('jiraTracker.workLog.fields.title')}
                            value={form.title}
                            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                            autoFocus
                        />
                        <Box className="weekly-work-log__form-row">
                            <TextField
                                fullWidth
                                size="small"
                                label={t('jiraTracker.workLog.fields.ticketId')}
                                value={form.ticketId}
                                onChange={(e) => setForm((prev) => ({ ...prev, ticketId: e.target.value }))}
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel id="work-log-period-mode-label">
                                    {t('jiraTracker.workLog.fields.periodMode')}
                                </InputLabel>
                                <Select
                                    labelId="work-log-period-mode-label"
                                    label={t('jiraTracker.workLog.fields.periodMode')}
                                    value={
                                        form.personalWorkLogEntryId && form.personalWorkLogEntryId > 0
                                            ? 'single'
                                            : form.periodMode
                                    }
                                    disabled={Boolean(form.personalWorkLogEntryId && form.personalWorkLogEntryId > 0)}
                                    onChange={(e) => {
                                        const mode = e.target.value as PeriodMode;
                                        setForm((prev) => ({
                                            ...prev,
                                            periodMode: mode,
                                            periodEndDate:
                                                mode === 'range' && prev.periodEndDate < prev.workDate
                                                    ? prev.workDate
                                                    : prev.periodEndDate || prev.workDate,
                                        }));
                                    }}
                                >
                                    <MenuItem value="single">{t('jiraTracker.workLog.period.single')}</MenuItem>
                                    <MenuItem value="range">{t('jiraTracker.workLog.period.range')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box className="weekly-work-log__form-row">
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label={t('jiraTracker.workLog.fields.date')}
                                slotProps={{ inputLabel: { shrink: true } }}
                                value={form.workDate}
                                onChange={(e) => {
                                    const nextStart = e.target.value;
                                    setForm((prev) => ({
                                        ...prev,
                                        workDate: nextStart,
                                        periodEndDate:
                                            prev.periodMode === 'range' && prev.periodEndDate < nextStart
                                                ? nextStart
                                                : prev.periodEndDate,
                                    }));
                                }}
                            />
                            {form.periodMode === 'range' &&
                            !(form.personalWorkLogEntryId && form.personalWorkLogEntryId > 0) ? (
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={t('jiraTracker.workLog.fields.periodEndDate')}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        htmlInput: { min: form.workDate },
                                    }}
                                    value={form.periodEndDate || form.workDate}
                                    onChange={(e) =>
                                        setForm((prev) => ({ ...prev, periodEndDate: e.target.value }))
                                    }
                                    helperText={
                                        form.periodEndDate && form.periodEndDate >= form.workDate
                                            ? t('jiraTracker.workLog.period.helper', {
                                                  count: enumerateDateKeys(form.workDate, form.periodEndDate)
                                                      .length,
                                              })
                                            : undefined
                                    }
                                />
                            ) : (
                                <Box />
                            )}
                        </Box>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={t('jiraTracker.workLog.fields.durationMinutes')}
                            value={form.durationMinutes}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    durationMinutes: Number(e.target.value) || 0,
                                }))
                            }
                            helperText={formatMinutes(form.durationMinutes)}
                        />
                        <Box>
                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                {t('jiraTracker.workLog.fields.priorityColor')}
                            </Typography>
                            <Box className="weekly-work-log__color-picker" role="group">
                                {PRIORITY_COLORS.map((color) => (
                                    <button
                                        key={color}
                                        type="button"
                                        className={`weekly-work-log__color-option weekly-work-log__color-option--${color}${
                                            form.priorityColor === color ? ' is-selected' : ''
                                        }`}
                                        title={t(`jiraTracker.workLog.priority.${color}`)}
                                        aria-label={t(`jiraTracker.workLog.priority.${color}`)}
                                        aria-pressed={form.priorityColor === color}
                                        onClick={() => setForm((prev) => ({ ...prev, priorityColor: color }))}
                                    />
                                ))}
                            </Box>
                        </Box>
                        <Accordion
                            className="weekly-work-log__tech-accordion"
                            expanded={technicalNotesExpanded}
                            onChange={(_, expanded) => setTechnicalNotesExpanded(expanded)}
                            disableGutters
                            slotProps={{
                                transition: {
                                    // Collapse ölçümü içerik yüksekliğini kesmesin
                                    timeout: 0,
                                    unmountOnExit: true,
                                },
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography className="weekly-work-log__tech-accordion-title">
                                    {t('jiraTracker.workLog.fields.technicalNotes')}
                                    {form.technicalNotes.trim() ? (
                                        <span className="weekly-work-log__tech-accordion-badge">•</span>
                                    ) : null}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box className="weekly-work-log__tech-notes-wrap">
                                    <textarea
                                        ref={technicalNotesRef}
                                        className="weekly-work-log__tech-notes-textarea"
                                        placeholder={t('jiraTracker.workLog.fields.technicalNotesPlaceholder')}
                                        value={form.technicalNotes}
                                        onChange={(e) => {
                                            setForm((prev) => ({ ...prev, technicalNotes: e.target.value }));
                                        }}
                                        onInput={syncTechnicalNotesHeight}
                                        spellCheck={false}
                                    />
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={3}
                            label={t('jiraTracker.workLog.fields.notes')}
                            value={form.notes}
                            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.isCompleted}
                                    onChange={(e) =>
                                        setForm((prev) => ({ ...prev, isCompleted: e.target.checked }))
                                    }
                                />
                            }
                            label={t('jiraTracker.workLog.fields.isCompleted')}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    {form.personalWorkLogEntryId ? (
                        <SCUIButton
                            type="error"
                            sizeType="default"
                            size="m"
                            text={t('jiraTracker.workLog.delete')}
                            onClick={handleDelete}
                            disabled={saving}
                        />
                    ) : (
                        <span />
                    )}
                    <Typography className="weekly-work-log__save-hint">
                        {t('jiraTracker.workLog.saveHint')}
                    </Typography>
                    <Box display="flex" gap={1}>
                        <SCUIButton
                            type="ghost"
                            sizeType="default"
                            size="m"
                            text={t('jiraTracker.workLog.cancel')}
                            onClick={closeModal}
                            disabled={saving}
                        />
                        <SCUIButton
                            type="primary"
                            sizeType="default"
                            size="m"
                            text={saving ? t('jiraTracker.loading') : t('jiraTracker.workLog.save')}
                            onClick={handleSave}
                            disabled={saving}
                        />
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WeeklyWorkLogPanel;
